import React, { useMemo, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Measure from "react-measure";
import {
	DataGrid,
	ProgressIndicator
} from "rhombus-react";

import { CreativesSocketListener } from "hooks/useCreativesSocket";
import { useOnMountFTMessage } from "hooks/Notification";

import { Creatives } from "reducers/Creatives";

import { CREATIVE_COLUMNS } from "widgets/grid/columns/Creatives";
import { getPredictiveScoreColumn } from "widgets/grid/cells/PredictiveScore";
import { useGridScrollUtils } from "utils/DataGrid";
import { CreativeRowRenderer } from "utils/columns/LockedRow";
import { MIN_GRID_HEIGHT, DEFAULT_ROW_HEIGHT } from "utils/constants";
import { getLibraryAutoClassificationEnabled, getManualClassificationEnabled } from "selectors/Intelligence";
import { usePredictiveScoringEnabled } from "hooks/usePredictiveScoringEnabled";
import { loadScores, loadLibraryKpis, PREDICTIVE_SCORE_ENTITY_TYPES } from "reducers/PredictiveScoring";
import { getPrimaryKpi } from "selectors/PredictiveScoring";
import { isPredictiveScoringTask, isFailedScoringTask } from "../../socket/formatters/predictiveScoringTask";

function mapCreatives(creatives, currentSelection, activeCreativeId){
	const activeIdString = activeCreativeId?.toString();

	return creatives.map((creative) => {
		const {
			id,
		} = creative;

		const totalFileSize = creative?.totalFileSize?.totalSize;

		return {
			...creative,
			totalFileSize,
			actions: id,
			// TODO: is this the best way to figure this out?  Maybe if we upgrade the grid we can use it's built in functionality
			isSelected: currentSelection.indexOf(creative.id) > -1,
			isActive:  activeIdString === creative.id.toString(),
		};
	});
}

function filterVisibleColumns(props = {}) {
	const { isLibraryAutoClassificationEnabled, isManualClassificationEnabled, isPredictiveScoringOn } = props;

	let columns = CREATIVE_COLUMNS;

	if (!isLibraryAutoClassificationEnabled || !isManualClassificationEnabled) {
		columns = columns.filter((column) => {
			if (column.key === "autoClassificationStatus") {
				return isLibraryAutoClassificationEnabled;
			} else if (column.key === "labelsCnt") {
				return isManualClassificationEnabled;
			}
			return true;
		});
	}

	if (isPredictiveScoringOn) {
		const decisioningIndex = columns.findIndex(col => col.key === "decisioningType");
		const insertIndex = decisioningIndex >= 0 ? decisioningIndex + 1 : columns.length;
		// libraryId + creativeId come from each creative row (data.creativeLibraryId / data.id) in getRowMetaData.
		const predictiveScoreColumn = getPredictiveScoreColumn({ entityType: PREDICTIVE_SCORE_ENTITY_TYPES.CREATIVE });
		columns = [...columns.slice(0, insertIndex), predictiveScoreColumn, ...columns.slice(insertIndex)];
	}

	return columns;
}

export default function Grid({ creatives=[], libraryId, sort="", isLoading }) {
	const dataGridRef = useGridScrollUtils([creatives])
		, dispatch = useDispatch()
		, [sortColumn, sortDirection] = sort.split(":")
		;
	const currentSelection = useSelector(state => state.selected.creatives);
	const activeCreativeId = useSelector(state => state.creatives.activeCreativeId);
	const isManualClassificationEnabled = useSelector(getManualClassificationEnabled);
	const isLibraryAutoClassificationEnabled = useSelector(getLibraryAutoClassificationEnabled);
	const isAdvertiserLoaded = useSelector(state => state.advertiserDetails?.isLoaded !== false);
	const isPredictiveScoringOn = usePredictiveScoringEnabled();
	const primaryKpiId = useSelector(state => getPrimaryKpi(state)?.idScoringKpi ?? null);

	const latestRef = useRef({ isPredictiveScoringOn, libraryId });
	latestRef.current = { isPredictiveScoringOn, libraryId };

	const onScoringNotification = useCallback((e) => {
		const { task, status } = e?.detail?.message || {};
		if (isFailedScoringTask(task) || (isPredictiveScoringTask(task) && /success/i.test(status))) {
			const { isPredictiveScoringOn: currentlyOn, libraryId: currentLibraryId } = latestRef.current;
			if (currentlyOn && currentLibraryId) {
				dispatch(loadScores(currentLibraryId, PREDICTIVE_SCORE_ENTITY_TYPES.CREATIVE, { skipCache: true }));
			}
		}
	}, [dispatch]);
	useOnMountFTMessage(onScoringNotification);

	const lastLoadedRef = useRef(null);
	useEffect(() => {
		if (isPredictiveScoringOn && libraryId) {
			const last = lastLoadedRef.current;
			const isSameLibraryAsLast = !!last && last.libraryId === libraryId;
			const isKpiSwitch = isSameLibraryAsLast
				&& last.primaryKpiId !== null && primaryKpiId !== null && last.primaryKpiId !== primaryKpiId;
			if (!isSameLibraryAsLast || isKpiSwitch) {
				dispatch(loadScores(libraryId, PREDICTIVE_SCORE_ENTITY_TYPES.CREATIVE, { skipCache: isKpiSwitch }));
			}
			const isNewLibrary = !!last && last.libraryId !== libraryId;
			lastLoadedRef.current = { libraryId, primaryKpiId: isNewLibrary ? null : primaryKpiId };
		}
	}, [dispatch, isPredictiveScoringOn, libraryId, primaryKpiId]);

	useEffect(() => {
		if (isPredictiveScoringOn && libraryId) {
			dispatch(loadLibraryKpis(libraryId));
		}
	}, [dispatch, isPredictiveScoringOn, libraryId]);

	const augmentedCreatives = useMemo(() => {
		return mapCreatives(creatives, currentSelection, activeCreativeId);
	}, [creatives, currentSelection, activeCreativeId]);

	const visibleColumns = useMemo(() => {
		if (!isAdvertiserLoaded) { return filterVisibleColumns({ isLibraryAutoClassificationEnabled, isManualClassificationEnabled, isPredictiveScoringOn: false }); }
		const visibleProps = { isLibraryAutoClassificationEnabled, isManualClassificationEnabled, isPredictiveScoringOn };
		return filterVisibleColumns(visibleProps);
	}, [isAdvertiserLoaded, isLibraryAutoClassificationEnabled, isManualClassificationEnabled, isPredictiveScoringOn]);

	function getCreativeRow(rowID) {
		return augmentedCreatives[rowID];
	}

	const dataGridProps={
		columns: visibleColumns,
		rowGetter: getCreativeRow,
		rowsCount: creatives.length,
		rowRenderer: CreativeRowRenderer,
		rowHeight: DEFAULT_ROW_HEIGHT,
		sortColumn,
		sortDirection,
		onGridSort: (col, dir) => dispatch(Creatives.setSort(col, dir)),
		enableCellAutoFocus: false, /*
			This is a ReactDataGrid setting that defaults to `true`, which seems to make it fight
			with anything else trying to utilize regular `tabindex` control flow, like blade forms.
		*/
	};

	return (
		<CreativesSocketListener>
			<Measure>
				{ (obj) => {
					let gridHeight = obj?.contentRect?.entry?.height || MIN_GRID_HEIGHT;
					return (
						<div className="measure-component" ref={ obj.measureRef }>
							<ProgressIndicator isLoading={ isLoading } />
							<div ref={dataGridRef}>
								<DataGrid
									{ ...dataGridProps }
									minHeight={ gridHeight }
								/>
							</div>
						</div>
					);
				} }
			</Measure>
		</CreativesSocketListener>
	);
}

export const PRIVATE = { filterVisibleColumns };

Grid.displayName = "Grid";
